import {
  addPackageByGroupId,
  getPackagesByGroupId,
} from "../../../../Redux/Groups/GroupsActions";
import validator, { ValidationField } from "../../../../Util/validator";
import { AppDispatch, RootState } from "../../../../Redux/store";
import Select, { components, SingleValue } from "react-select";
import toastHelper from "../../../../Util/toastHelper";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../Common/Loading/Loading";
import { RiDeleteBinLine } from "react-icons/ri";
import styles from "./AddPackage.module.scss";
import React, { useState } from "react";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  groupId: string;
}

interface Amenity {
  amenityId: number;
  name: string;
  status: string;
  image: string;
  requiredBooking: boolean;
}

interface SelectOption {
  value: number;
  label: string;
  data: Amenity;
}

enum AllowTo {
  ALL = "all",
  MEMBERS = "members",
  NON_MEMBERS = "non-members",
}

type Period = "monthly" | "quarterly" | "halfyearly" | "yearly";

const PERIOD_OPTIONS: { period: Period; label: string }[] = [
  { period: "monthly", label: "Monthly" },
  { period: "quarterly", label: "Quarterly" },
  { period: "halfyearly", label: "Half Yearly" },
  { period: "yearly", label: "Yearly" },
];

const defaultPackageRates = (): Record<
  Period,
  { enabled: boolean; rates: string; discountRate: string }
> => ({
  monthly: { enabled: false, rates: "", discountRate: "0" },
  quarterly: { enabled: false, rates: "", discountRate: "0" },
  halfyearly: { enabled: false, rates: "", discountRate: "0" },
  yearly: { enabled: false, rates: "", discountRate: "0" },
});

function AddPackage(props: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { setIsOpen, groupId } = props;
  const { addPackageByGroupIdLoading, getAmenitiesByGroupIdData } = useSelector(
    (state: RootState) => state.groups,
  );
  const [packageName, setPackageName] = useState("");
  const [packageDescription, setPackageDescription] = useState("");
  const [status, setStatus] = useState("true");
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
  const [inputValue, setInputValue] = useState<SelectOption | null>(null);
  const [freeTrial, setFreeTrial] = useState(false);
  const [trialDays, setTrialDays] = useState("");
  const [isFamily, setIsFamily] = useState(false);
  const [noOfMember, setNoOfMember] = useState("");
  const [allowTo, setAllowTo] = useState<AllowTo>(AllowTo.ALL);
  const [packageRates, setPackageRates] = useState(defaultPackageRates);
  const [errors, setErrors] = useState<{
    packageName: string;
    packageDescription: string;
    amenityIds: string;
    status: string;
    price: string;
    noOfMember: string;
    trialDays: string;
    packageRates: string;
  }>({
    packageName: "",
    packageDescription: "",
    amenityIds: "",
    status: "",
    price: "",
    noOfMember: "",
    trialDays: "",
    packageRates: "",
  });

  const options = getAmenitiesByGroupIdData
    ?.filter((item) => item?.amenityTypeName?.toLowerCase() !== "parking")
    ?.map((item) => ({
      value: item.amenityId,
      label: item.name,
      data: item,
    }));

  const handleChange = (selectedOption: SingleValue<SelectOption>) => {
    if (!selectedOption) return;

    const alreadyAdded = selectedAmenities.find(
      (item: Amenity) => item.amenityId === selectedOption.data.amenityId,
    );

    if (!alreadyAdded) {
      setSelectedAmenities((prev) => [...prev, { ...selectedOption.data }]);
    }

    setInputValue(null);
  };

  const CustomOption = (props: any) => {
    return (
      <components.Option {...props} className={styles.dropdownItem}>
        {props?.data?.label}
      </components.Option>
    );
  };

  const deleteAmenityHandler = (amenityId: number) => {
    setSelectedAmenities((prev) =>
      prev.filter((item) => item.amenityId !== amenityId),
    );
  };

  const togglePeriod = (period: Period, enabled: boolean) => {
    setPackageRates((prev) => ({
      ...prev,
      [period]: {
        enabled,
        rates: enabled ? prev[period].rates : "",
        discountRate: enabled ? prev[period].discountRate || "0" : "0",
      },
    }));
  };

  const updatePeriodRate = (period: Period, rates: string) => {
    if (!/^\d*\.?\d{0,2}$/.test(rates)) return;
    setPackageRates((prev) => ({
      ...prev,
      [period]: { ...prev[period], rates },
    }));
  };

  const updatePeriodDiscountRate = (period: Period, discountRate: string) => {
    if (!/^\d*\.?\d{0,2}$/.test(discountRate)) return;
    setPackageRates((prev) => ({
      ...prev,
      [period]: { ...prev[period], discountRate },
    }));
  };

  const getSelectedPackageRates = () =>
    PERIOD_OPTIONS.filter(({ period }) => packageRates[period].enabled).map(
      ({ period }) => ({
        period,
        rates: Number(packageRates[period].rates),
        discountRate: Number(packageRates[period].discountRate || 0),
      }),
    );

  const validatePackageRates = () => {
    const selected = PERIOD_OPTIONS.filter(
      ({ period }) => packageRates[period].enabled,
    );

    if (selected.length === 0) {
      return "Select at least one billing period";
    }

    const missingRate = selected.find(
      ({ period }) =>
        !packageRates[period].rates || Number(packageRates[period].rates) <= 0,
    );

    if (missingRate) {
      return `Enter a valid rate for ${missingRate.label}`;
    }

    return "";
  };

  const addPackageHandler = () => {
    let fieldsArray = [
      {
        key: "packageName",
        value: packageName,
        type: "text",
      },

      {
        key: "packageDescription",
        value: packageDescription,
        type: "text",
      },
      {
        key: "amenityIds",
        value: selectedAmenities.map((item) => item.amenityId),
        type: "array",
      },
      {
        key: "status",
        value: status,
        type: "text",
      },
      {
        key: "allowTo",
        value: allowTo,
        type: "text",
      },
    ];

    if (isFamily) {
      fieldsArray.push({
        key: "noOfMember",
        value: noOfMember,
        type: "numberOnly",
      });
    }

    if (freeTrial) {
      fieldsArray.push({
        key: "trialDays",
        value: trialDays,
        type: "numberOnly",
      });
    }

    const { errors: fieldsErrors, isValid } = validator(
      fieldsArray as ValidationField[],
    );

    const packageRatesError = validatePackageRates();

    setErrors({
      ...errors,
      ...fieldsErrors,
      packageRates: packageRatesError,
    });

    if (isValid && !packageRatesError) {
      if (!addPackageByGroupIdLoading) {
        const successHandler = () => {
          setIsOpen(false);
          if (groupId) {
            dispatch(
              getPackagesByGroupId({
                groupId: groupId,
              }),
            );
          }
          toastHelper.success("Package added successfully");
        };

        if (groupId) {
          dispatch(
            addPackageByGroupId({
              packageName,
              packageDescription: packageDescription,
              groupId: Number(groupId),
              status: status === "true" ? true : false,
              amenityIds: selectedAmenities.map((item) => item?.amenityId),
              isFreeMonth: freeTrial,
              trialDays: freeTrial && trialDays ? Number(trialDays) : 0,
              isFamily: isFamily,
              noOfMember: noOfMember ? Number(noOfMember) : 0,
              packageRates: getSelectedPackageRates(),
              isMember:
                allowTo === AllowTo.MEMBERS || allowTo === AllowTo.ALL
                  ? true
                  : false,
              isNonMember:
                allowTo === AllowTo.NON_MEMBERS || allowTo === AllowTo.ALL
                  ? true
                  : false,
              successHandler,
            }),
          );
        }
      }
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>Add Package</h1>
      </div>
      <form className={styles.formContainer}>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Package Name</p>
          <input
            type="text"
            className={styles.input}
            value={packageName}
            onChange={(e) => setPackageName(e.target.value)}
          />
          <p className={styles.error}>{errors.packageName}</p>
        </div>
        <div className={styles.textAreaContainer}>
          <p className={styles.label}>Package Description</p>
          <textarea
            className={styles.textArea}
            value={packageDescription}
            onChange={(e) => setPackageDescription(e.target.value)}
            rows={4}
          />
          <p className={styles.error}>{errors.packageDescription}</p>
        </div>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Status</p>
          <select
            className={styles.input}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <p className={styles.error}>{errors.status}</p>
        </div>

        <div className={styles.packageRatesContainer}>
          <p className={styles.label}>Package Rates</p>
          <div className={styles.packageRatesList}>
            {PERIOD_OPTIONS.map(({ period, label }) => (
              <div className={styles.packageRateRow} key={period}>
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={packageRates[period].enabled}
                    onChange={(e) => togglePeriod(period, e.target.checked)}
                  />
                  <p className={styles.periodLabel}>{label}</p>
                </div>
                <div
                  className={`${styles.rateInputContainer} ${
                    !packageRates[period].enabled ? styles.disabled : ""
                  }`}
                >
                  <p className={styles.currency}>$</p>
                  <input
                    type="text"
                    className={styles.input}
                    value={packageRates[period].rates}
                    disabled={!packageRates[period].enabled}
                    placeholder="Rate"
                    onChange={(e) => updatePeriodRate(period, e.target.value)}
                  />
                </div>
                <div
                  className={`${styles.rateInputContainer} ${
                    !packageRates[period].enabled ? styles.disabled : ""
                  }`}
                >
                  <p className={styles.currency}>$</p>
                  <input
                    type="text"
                    className={styles.input}
                    value={packageRates[period].discountRate}
                    disabled={!packageRates[period].enabled}
                    placeholder="Discount"
                    onChange={(e) =>
                      updatePeriodDiscountRate(period, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
          <p className={styles.error}>{errors.packageRates}</p>
        </div>
        <div className={styles.selectorContainer}>
          <p className={styles.label}>Amenities</p>
          <div className={styles.packageListContainer}>
            {selectedAmenities.map((item, i: number) => (
              <div className={styles.packageItem} key={i}>
                <div className={styles.labelContainer}>
                  <p className={styles.label}>{item.name}</p>
                </div>
                <div className={styles.rightContainer}></div>
                <div className={styles.iconContainer}>
                  <RiDeleteBinLine
                    className={styles.icon}
                    onClick={() => deleteAmenityHandler(item.amenityId)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.searchAmenitiesSelect}>
            <Select
              options={options}
              value={inputValue}
              onChange={handleChange}
              onInputChange={() => {}}
              placeholder="Search amenities..."
              getOptionLabel={(e) => e.label}
              // className={styles.searchAmenitiesSelect}
              classNamePrefix="my-select"
              components={{
                Option: CustomOption,
              }}
              formatOptionLabel={(e) => (
                <div className={styles.AmenitiesLabel}>{e.label}</div>
              )}
            />
            <p className={styles.error}>{errors.amenityIds}</p>
          </div>
        </div>
        <div className={styles.checkboxWrapperContainer}>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={freeTrial}
              onChange={(e) => {
                setFreeTrial(e.target.checked);
                if (!e.target.checked) {
                  setTrialDays("");
                }
              }}
            />
            <p className={styles.label}>Free Trial</p>
          </div>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={isFamily}
              onChange={(e) => setIsFamily(e.target.checked)}
            />
            <p className={styles.label}>Is Family Package</p>
          </div>
        </div>
        {freeTrial && (
          <div className={styles.priceInputContainer}>
            <p className={styles.label}>Trial Days</p>
            <div className={styles.inputContainer}>
              <input
                type="text"
                className={styles.input}
                value={trialDays}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setTrialDays(value);
                  }
                }}
              />
            </div>
            <p className={styles.error}>{errors.trialDays}</p>
          </div>
        )}
        {isFamily && (
          <div className={styles.priceInputContainer}>
            <p className={styles.label}>No of Members</p>
            <div className={styles.inputContainer}>
              <input
                type="text"
                className={styles.input}
                value={noOfMember}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setNoOfMember(value);
                  }
                }}
              />
            </div>
            <p className={styles.error}>{errors.noOfMember}</p>
          </div>
        )}
        <div className={styles.inputContainer}>
          <p className={styles.label}>Allowed To</p>
          <div className={styles.checkboxesContainer}>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === AllowTo.ALL}
                onChange={(e) => setAllowTo(AllowTo.ALL)}
              />
              <p className={styles.label}>All</p>
            </div>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === AllowTo.MEMBERS}
                onChange={(e) => setAllowTo(AllowTo.MEMBERS)}
              />
              <p className={styles.label}>Members</p>
            </div>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === AllowTo.NON_MEMBERS}
                onChange={(e) => setAllowTo(AllowTo.NON_MEMBERS)}
              />
              <p className={styles.label}>Non Members</p>
            </div>
          </div>
        </div>
        {addPackageByGroupIdLoading && <Loading />}
      </form>

      <div className={styles.buttonContainer}>
        <button className={styles.button} onClick={addPackageHandler}>
          Add
        </button>
        <button className={styles.cancelBtn} onClick={() => setIsOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AddPackage;
